/**
 * Prop builders for FlowchartView child components
 * 
 * These functions construct the props objects for child components,
 * keeping the main FlowchartView index.js clean and readable.
 */

/**
 * Build props for FlowchartOverlays component
 */
export function buildOverlaysProps({
  props,
  core,
  handlers,
  hoveredMissionId,
  handleMissionHover,
  handleMissionLeave,
  initialFocusedTaskIds,
}) {
  return {
    sprintName: props.sprintName,
    sprintIcon: props.sprintIcon,
    sprintProgress: props.sprintProgress,
    breadcrumbPath: props.breadcrumbPath,
    campaignId: props.campaignId,
    tasks: props.tasks,
    displayTasks: core.displayTasks,
    displayPositions: core.displayPositions,
    displayEdges: core.displayEdges,
    nodeDimensions: core.nodeDimensions,
    statuses: props.statuses,
    lockStates: core.lockStates,
    nestedSprints: props.nestedSprints,
    allSprints: props.allSprints,
    allCategories: props.allCategories,
    allTags: props.allTags,
    pan: core.pan,
    zoom: core.zoom,
    containerRef: core.containerRef,
    contentBounds: core.contentBounds,
    focusContentBounds: core.focusContentBounds,
    selectedEdge: core.selectedEdge,
    setSelectedNodes: core.setSelectedNodes,
    isFocusMode: core.isFocusMode,
    focusedTaskIds: core.focusedTaskIds,
    allFocusedTaskIds: core.allFocusedTaskIds,
    isAnimatingIntoFocus: core.isAnimatingIntoFocus,
    isAnimatingOutOfFocus: core.isAnimatingOutOfFocus,
    handleExitFocusMode: core.handleExitFocusMode,
    isRestoringFokusState: !!initialFocusedTaskIds,
    isPanning: core.isPanning,
    isDragging: core.isDragging,
    isConnecting: core.isConnecting,
    autoArrangeBlocked: core.autoArrangeBlocked,
    pendingNestAction: core.pendingNestAction,
    confirmNestAction: core.confirmNestAction,
    cancelNestAction: core.cancelNestAction,
    onOpenDetails: handlers.onOpenDetails,
    onOpenAttachments: handlers.onOpenAttachments,
    centerOnTask: core.centerOnTask,
    setPan: core.setPan,
    setPanAnimated: core.setPanAnimated,
    constrainPan: core.constrainPan,
    onNavigateToParent: handlers.onNavigateToParent,
    onSelectSprint: handlers.onSelectSprint,
    onRenameSprint: handlers.onRenameSprint,
    onSprintIconChange: handlers.onSprintIconChange,
    onStatusChange: handlers.onStatusChange,
    onCategoryChange: handlers.onCategoryChange,
    onNestTasks: handlers.onNestTasks,
    isMultiCampaignMode: props.isMultiCampaignMode,
    activeView: props.activeView,
    multiCampaignCampaigns: props.multiCampaignCampaigns,
    onViewRename: handlers.onViewRename,
    onViewIconChange: handlers.onViewIconChange,
    flowViewVisibility: props.flowViewVisibility,
    onFlowViewVisibilityChange: handlers.onFlowViewVisibilityChange,
    hideViewExplainerModal: props.hideViewExplainerModal,
    onHideViewExplainerModalChange: handlers.onHideViewExplainerModalChange,
    // List row hover is CSS-only; only wire canvas highlight callbacks
    onMissionHover: handleMissionHover,
    onMissionLeave: handleMissionLeave,
  };
}

/**
 * Build props for FlowchartCanvas component
 */
export function buildCanvasProps({
  props,
  core,
  handlers,
  handleChainMoveLeftWithSwap,
  handleChainMoveRightWithSwap,
  hoveredMissionId,
  handleMissionHover,
  handleMissionLeave,
}) {
  return {
    pan: core.pan,
    zoom: core.zoom,
    tasks: props.tasks,
    tasksToRender: core.tasksToRender,
    fullNodeIds: core.fullNodeIds,
    displayTasks: core.displayTasks,
    positionsToUse: core.positionsToUse,
    displayPositions: core.displayPositions,
    nodeDimensions: core.nodeDimensions,
    viewportSize: core.containerRectRef.current,
    edgesToRender: core.edgesToRender,
    selectedEdge: core.selectedEdge,
    displayEdges: core.displayEdges,
    hiddenConnections: core.hiddenConnections,
    selectedNodes: core.selectedNodes,
    selectionBox: core.selectionBox,
    draggingNode: core.draggingNode,
    dropTarget: core.dropTarget,
    isConnecting: core.isConnecting,
    connectingFrom: core.connectingFrom,
    connectingTarget: core.connectingTarget,
    connectingBranch: core.connectingBranch,
    connectingStartPos: core.connectingStartPos,
    reconnectingEdge: core.reconnectingEdge,
    mousePos: core.mousePos,
    isFocusMode: core.isFocusMode,
    focusedTaskIds: core.focusedTaskIds,
    isAnimatingLayout: core.isAnimatingLayout,
    isAnimatingIntoFocus: core.isAnimatingIntoFocus,
    isAnimatingOutOfFocus: core.isAnimatingOutOfFocus,
    statuses: props.statuses,
    lockStates: core.lockStates,
    allCategories: props.allCategories,
    allTags: props.allTags,
    nestedSprints: props.nestedSprints,
    allSprints: props.allSprints,
    containerRef: core.containerRef,
    contentBounds: core.contentBounds,
    focusContentBounds: core.focusContentBounds,
    getNodeAnimationState: core.getNodeAnimationState,
    handleNodeDragStart: core.handleNodeDragStart,
    handleNodeClick: core.handleNodeClick,
    handleConnectionDragStart: core.handleConnectionDragStart,
    handleEdgeArrowDragStart: core.handleEdgeArrowDragStart,
    handleCreateConnectedNode: core.handleCreateConnectedNode,
    hasClipboardData: core.hasClipboardData,
    handleFocusTask: core.handleFocusTask,
    centerOnTask: core.centerOnTask,
    handleDimensionsChange: core.handleDimensionsChange,
    handleEdgeClick: core.handleEdgeClick,
    handleInsertNodeOnEdge: core.handleInsertNodeOnEdge,
    onRenameTask: handlers.onRenameTask,
    onStatusChange: handlers.onStatusChange,
    onCategoryChange: handlers.onCategoryChange,
    onAddTag: handlers.onAddTag,
    onRemoveTag: handlers.onRemoveTag,
    onSetPrimaryTag: handlers.onSetPrimaryTag,
    onIconChange: handlers.onIconChange,
    onIconDeleted: handlers.onIconDeleted,
    onTaskResize: handlers.onTaskResize,
    onTaskResizeEnd: handlers.onTaskResizeEnd,
    onTaskResetSize: handlers.onTaskResetSize,
    onDecisionAnswerChange: handlers.onDecisionAnswerChange,
    onDecisionOptionsChange: handlers.onDecisionOptionsChange,
    onDeleteTask: handlers.onDeleteTask,
    onOpenNestedSprint: handlers.onOpenNestedSprint,
    onCreateNestedSprint: handlers.onCreateNestedSprint,
    onRemoveNestedSprint: handlers.onRemoveNestedSprint,
    onDeliverablesChange: handlers.onDeliverablesChange,
    onInputsChange: handlers.onInputsChange,
    onOpenDetails: handlers.onOpenDetails,
    onOpenAttachments: handlers.onOpenAttachments,
    onAddStatus: handlers.onAddStatus,
    onDeleteStatus: handlers.onDeleteStatus,
    onRenameStatus: handlers.onRenameStatus,
    onSetStatusAutoSync: handlers.onSetStatusAutoSync,
    onMilestoneAnchorChange: handlers.onMilestoneAnchorChange,
    onAddMilestoneOnHost: handlers.onAddMilestoneOnHost,
    onAddNoteOnHost: handlers.onAddNoteOnHost,
    onCreateParallelMission: handlers.onCreateParallelMission,
    onInsertMissionBefore: handlers.onInsertMissionBefore,
    onDeleteCategory: handlers.onDeleteCategory,
    onRenameTag: handlers.onRenameTag,
    onDeleteTag: handlers.onDeleteTag,
    onDueDateChange: handlers.onDueDateChange,
    onRemindersChange: handlers.onRemindersChange,
    onEstimatedDurationMinutesChange: handlers.onEstimatedDurationMinutesChange,
    onCreatedAtChange: handlers.onCreatedAtChange,
    onStartedAtChange: handlers.onStartedAtChange,
    onCompletedAtChange: handlers.onCompletedAtChange,
    onUpdatedAtChange: handlers.onUpdatedAtChange,
    onPriorityChange: handlers.onPriorityChange,
    onDifficultyChange: handlers.onDifficultyChange,
    onCustomFieldValueChange: handlers.onCustomFieldValueChange,
    customFields: props.customFields,
    onArchiveTask: handlers.onArchiveTask,
    onUnarchiveTask: handlers.onUnarchiveTask,
    onDependencyChange: handlers.onDependencyChange,
    onConditionsChange: handlers.onConditionsChange,
    onConditionsDialogOpen: core.handleConditionsDialogOpen,
    onConditionsDialogClose: core.handleConditionsDialogClose,
    onCopy: core.copyNode,
    onDuplicate: core.duplicateNode,
    cardDisplaySettings: props.cardDisplaySettings,
    isTemplate: props.isTemplate,
    isMultiCampaignMode: props.isMultiCampaignMode,
    multiCampaignCampaigns: props.multiCampaignCampaigns,
    onSelectSprint: handlers.onSelectSprint,
    allSprints: props.allSprints,
    onRemoveCampaign: handlers.onRemoveCampaign,
    removingCampaignId: props.removingCampaignId,
    chainInfo: core.chainInfo,
    chains: props.chains,
    onChainMoveLeft: handleChainMoveLeftWithSwap,
    onChainMoveRight: handleChainMoveRightWithSwap,
    currentView: props.currentView,
    customViews: props.customViews,
    activeCustomViewId: props.activeCustomViewId,
    onViewChange: handlers.onViewChange,
    onSelectCustomView: handlers.onSelectCustomView,
    onGoToTaskInView: handlers.onGoToTaskInView,
    onExplodeNestedCampaign: handlers.onExplodeNestedCampaign,
    onShowAddMissionArrow: handlers.onShowAddMissionArrow,
    hoveredMissionId,
    onMissionHover: handleMissionHover,
    onMissionLeave: handleMissionLeave,
    onSubmitDeliverable: handlers.onSubmitDeliverable,
    onEditDeliverables: handlers.onEditDeliverables,
    onClearDeliverable: handlers.onClearDeliverable,
    layoutSurfaceEpoch: core.layoutSurfaceEpoch,
  };
}

/**
 * Build props for HandlesLayer component
 */
export function buildHandlesLayerProps({ props, core, handlers }) {
  return {
    tasks: core.tasksToRender,
    allTasks: props.tasks,
    nodePositions: core.positionsToUse,
    nodeDimensions: core.nodeDimensions,
    edges: core.edgesToRender,
    hoveredNodeId: core.hoveredNodeId,
    selectedNodes: core.selectedNodes,
    containerRef: core.containerRef,
    pan: core.pan,
    zoom: core.zoom,
    isDragging: core.isDragging,
    draggingNode: core.draggingNode,
    onConnectionDragStart: core.handleConnectionDragStart,
    onCreateConnectedNode: core.handleCreateConnectedNode,
    onInsertMissionBefore: handlers.onInsertMissionBefore,
    onPasteBeforeHost: core.pasteBeforeHost,
    checkClipboard: core.hasClipboardData,
    onCreateCampaign: props.onCreateCampaign,
    onInsertNode: core.handleInsertNodeOnEdge,
    onNodeDragStart: core.handleNodeDragStart,
    isMultiCampaignMode: props.isMultiCampaignMode,
    layoutSurfaceEpoch: core.layoutSurfaceEpoch,
  };
}

/**
 * Build props for BulkActionsToolbar component
 */
export function buildBulkActionsProps({ props, core, handlers, handleClearSelection }) {
  return {
    selectedCount: core.selectedNodes.size,
    selectedTaskIds: Array.from(core.selectedNodes),
    tasks: props.tasks,
    statuses: props.statuses,
    allTags: props.allTags,
    customFields: props.customFields,
    onStatusChange: handlers.onStatusChange,
    onPriorityChange: handlers.onPriorityChange,
    onDifficultyChange: handlers.onDifficultyChange,
    onIconChange: handlers.onIconChange,
    onSetPrimaryTag: handlers.onSetPrimaryTag,
    onAddTag: handlers.onAddTag,
    onRemoveTag: handlers.onRemoveTag,
    onArchiveTasks: handlers.onArchiveTasks,
    onUnarchiveTask: handlers.onUnarchiveTask,
    onDueDateChange: handlers.onDueDateChange,
    onEstimatedDurationMinutesChange: handlers.onEstimatedDurationMinutesChange,
    onStartDateChange: handlers.onStartedAtChange,
    onCustomFieldValueChange: handlers.onCustomFieldValueChange,
    onClearSelection: handleClearSelection,
    flowBulkCompileNestedCampaign: true,
    isMultiCampaignMode: !!props.isMultiCampaignMode,
    onCompileMissionsIntoNestedCampaign: handlers.onCompileMissionsIntoNestedCampaign,
  };
}
